import {
  BallFinalKinds,
  HitTypes,
  IApiBatter,
  IApiCatcher,
  IApiPitcher,
  IBallFinal,
  IPlayer,
  iPlayerToApiPlayerWithDexterityMap,
  TopBottom,
} from '@trackman/baseball-shared-components';
import { IPlayPreviewRow } from 'interfaces/sessionVerification/plays/IPlayPreviewRow';
import moment from 'moment';
import { IPlayPreviewRowMetrics } from '../interfaces/sessionVerification/plays/IPlayPreviewRow';
import { IMergedPlayWithBallFinal, IPlayWithBallFinal } from '../interfaces/sessionVerification/plays/IPlayWithBallFinal';

const mapPlayWithBallFinalToReviewRow = (play: IMergedPlayWithBallFinal, homeTeamName: string, awayTeamName: string) => {
  const obj = {
    id: play.play.id,
    playId: play.play.playId,
    balls: play.play.gameState.balls,
    batter: {
      id: play.play.players.batter.id,
      isHomeTeam: play.play.gameState.isHomeTeamBatting,
      name: play.play.players.batter.nameRef,
      teamName: play.play.gameState.isHomeTeamBatting ? homeTeamName : awayTeamName,
      position: play.play.players.batter.position,
    },
    batterDexterity: play.play.players.batter.battingHandedness,
    hitType: apiToReviewRowHitTypeMap(play),
    inning: {
      inningNumber: play.play.gameState.inning,
      isTopInning: play.play.gameState.topBottom === TopBottom.Top,
    },
    korbb: play.play.kOrBB,
    note: play.play.note,
    outs: play.play.gameState.outs,
    outsOnPlay: play.play.playResult.outs,
    pitchCall: play.play.pitchTag.pitchCall,
    playSequenceNumber: play.play.taggerBehavior.playSequenceNumber,
    sequenceNumber: play.play.taggerBehavior.sequenceNumber,
    pitcher: {
      id: play.play.players.pitcher.id,
      isHomeTeam: !play.play.gameState.isHomeTeamBatting,
      name: play.play.players.pitcher.nameRef,
      teamName: play.play.gameState.isHomeTeamBatting ? awayTeamName : homeTeamName,
      position: play.play.players.pitcher.position,
    },
    catcher: {
      id: play.play.players.catcher.id,
      isHomeTeam: !play.play.gameState.isHomeTeamBatting,
      name: play.play.players.catcher.nameRef,
      teamName: play.play.gameState.isHomeTeamBatting ? awayTeamName : homeTeamName,
      position: play.play.players.catcher.position,
    },
    pitcherDexterity: play.play.players.pitcher.pitchingHandedness,
    strikes: play.play.gameState.strikes,
    taggedPitchType: play.play.pitchTag.pitchType,
    runsScored: play.play.playResult.runs,
    isAddedManually: play.play.taggerBehavior.manuallyAddedByTagger,
    isWarmup: play.play.taggerBehavior.isWarmup,
    isDeleted: play.play.taggerBehavior.isDeleted,
    homeTeamScore: play.play.gameState.homeTeamScore,
    awayTeamScore: play.play.gameState.awayTeamScore,
    playResult: play.play.playResult.result,
    pitchBallFinalTrackId: play.play.pitchTag.ballFinalTrackId,
    hitBallFinalTrackId: play.play.hitTag && play.play.hitTag.ballFinalTrackId,
    catcherThrowBallFinalTrackId: play.play.catcherThrowTag && play.play.catcherThrowTag.ballFinalTrackId,
    pitchOfPA: play.play.taggerBehavior.pitchOfPA,
    pAofInning: play.play.taggerBehavior.pAofInning,
    status: {
      errors: [],
      status: '',
    },
    ...mapBallFinalToReviewRowMetrics(play.ballFinals),
    isChanged: play.isChanged,
    isBallTrackingChanged: play.isBallTrackingChanged,
    isPlayerTrackingChanged: play.isPlayerTrackingChanged,
    isMarkedForDeletion: play.isMarkedForDeletion,
    isCreated: play.isCreated,
    changedValues: play.changedValues,
  } as IPlayPreviewRow;

  return obj;
};

const mapBallFinalToReviewRowMetrics = (ballFinals: IBallFinal[]) => {
  const metrics = {} as IPlayPreviewRowMetrics;

  for (let i = 0; i < ballFinals.length; i++) {
    const ballFinal = ballFinals[i];
    switch (ballFinal.kind) {
      case BallFinalKinds.Pitch:
        metrics.verticalReleaseAngle = ballFinal?.pitch?.release?.verticalAngle;
        metrics.horizontalReleaseAngle = ballFinal.pitch && ballFinal.pitch.release.horizontalAngle;
        metrics.spinRate = ballFinal.start.spinRate;
        metrics.spinAxis = ballFinal.pitch && ballFinal.pitch.movement && ballFinal.pitch.movement.spinAxis;
        metrics.extension = ballFinal.pitch && ballFinal.pitch.release.extension;
        metrics.verticalBreak = ballFinal.pitch && ballFinal.pitch.movement && ballFinal.pitch.movement.vertical;
        metrics.horizontalBreak = ballFinal.pitch && ballFinal.pitch.movement && ballFinal.pitch.movement.horizontal;
        metrics.releaseSpeed = ballFinal.start.speed;
        metrics.tilt = ballFinal.pitch && ballFinal.pitch.movement && ballFinal.pitch.movement.spinAxis;
        metrics.releaseHeight = ballFinal.start.position.y;
        metrics.releaseSide = -ballFinal.start.position.z;
        metrics.inducedVerticalBreak = ballFinal.pitch && ballFinal.pitch.movement && ballFinal.pitch.movement.inducedVertical;
        metrics.plateLocHeight = ballFinal.pitch && ballFinal.pitch.location.position.y;
        metrics.plateLocSide = ballFinal.pitch && -ballFinal.pitch.location.position.z;
        break;
      case BallFinalKinds.Hit:
        metrics.hitExitSpeed = ballFinal.start.speed;
        metrics.hitEstCarry = ballFinal.hit ? ballFinal.hit.carry : 0;
        metrics.hitDirection = ballFinal.hit ? ballFinal.hit.launch.horizontalAngle : 0;
        metrics.hitBearing = ballFinal.hit ? ballFinal.hit.bearing : 0;
        break;
      default:
        break;
    }
  }
  return metrics;
};

const apiToReviewRowHitTypeMap = (play: IPlayWithBallFinal) => {
  if (play.play.hitTag) {
    if (!play.play.hitTag.hitType) {
      return HitTypes.Undefined;
    }
    return play.play.hitTag.hitType;
  }
  return HitTypes.Undefined;
};

const timeDisplay = (time: string) => {
  return moment.parseZone(time).format('YYYY-MM-DD HH:mm');
};

const playerToEditRowDisplayPlayerMap = (player: IPlayer | undefined, teamName: string) => {
  const apiPlayer = iPlayerToApiPlayerWithDexterityMap(player);
  return { ...apiPlayer, teamName, name: apiPlayer.nameRef };
};

const apiPlayerToPlayerMap = (player: IApiCatcher | IApiBatter | IApiPitcher): IPlayer => {
  return {
    id: player.id,
    firstName: player.nameRef.split(', ')[1],
    lastName: player.nameRef.split(', ')[0],
    positions: [player.position],
    dexterity: {
      bats: 'battingHandedness' in player ? (player as IApiBatter).battingHandedness : undefined,
      throws: 'pitchingHandedness' in player ? (player as IApiPitcher | IApiCatcher).pitchingHandedness : undefined,
    },
    isPlaceholder: player.isPlaceholder,
    foreignIds: player.foreignIds,
    jerseyNumber: player.jerseyNumber,
    height: player.height,
    strikeZoneBottom: player.strikeZoneBottom,
    strikeZoneTop: player.strikeZoneTop,
  };
};

export default {
  mapPlayWithBallFinalToReviewRow,
  timeDisplay,
  playerToEditRowDisplayPlayerMap,
  apiPlayerToPlayerMap,
};
